import { SpotlightCollection } from '@pcln/cards'
import { useContentfulWithPredicate } from '@pcln/contentful-components'
import { useFeature } from '@pcln/feature-components'
import { Box, Container } from 'pcln-design-system'
import { useEffect } from 'react'

import AdBanner from '@/components/AdBanner'
import AppDownloadOptIn from '@/components/AppDownloadOptIn'
import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import ContentfulOrderWrapper from '@/components/ContentfulOrderWrapper/ContentfulOrderWrapper'
import EmailSignUp from '@/components/EmailSignUp'
import { ExpressDealDialogExperiment } from '@/components/Hotels/ExpressDeal/ExpressDealDialogExperiment'
import LayoutComponentWrapper from '@/components/LayoutComponentWrapper'
import PromoBanners from '@/components/MerchModuleWrapper/PromoBanners'
import { MediaCardCollection } from '@/components/Promos/MediaCard/MediaCardCollection'
import MediaCardComponentHorizon from '@/components/Promos/MediaCard/MediaCardComponentHorizon'
import LinksWidget from '@/components/SkyMondeSeo/LinksWidget'
import WhyPricelineBanner from '@/components/WhyPricelineBanner/WhyPricelineBanner'
import { REASONS_HOME } from '@/constants/reasons'
import useBootstrapData from '@/hooks/useBootstrapData'
import useContentfulVariation from '@/hooks/useContentfulVariation'
import useExpressDealDialog from '@/hooks/useExpressDealDialog'
import analytics from '@/shared-utils/analytics'
import {
  isCollectionOfIMediaCard,
  isCollectionOfIMerchModule,
  isISpotlightCollection,
} from '@/shared-utils/content-model-type-guards'
import mapSpotlightCollectionData from '@/shared-utils/map-spotlight-collection-data'

import TripDisplayOrder from './TripDisplayOrder'

const expressDealQSKey = 'expressdeal'
const expressDealQSValue = 'y'

function AdBannerContent() {
  return (
    <AdBanner>
      <div id='div-gpt-ad-1614716188275-0' />
    </AdBanner>
  )
}

export default function HomeContent() {
  const { skymondeSEO } = useBootstrapData()

  const { isOpen, closeDialog } = useExpressDealDialog(expressDealQSKey, expressDealQSValue)
  const spotlightCollectionContentfulData = useContentfulVariation(
    'spotlightCollection',
    isISpotlightCollection,
  )

  const promoCollection = useContentfulWithPredicate('promoCollection', isCollectionOfIMerchModule)
  const promoItems = promoCollection?.items || ([] as const)

  const mediaCardCollectionData = useContentfulVariation('mediaCardPromoCollection', isCollectionOfIMediaCard)

  const mappedSpotlightCollectionData = spotlightCollectionContentfulData
    ? mapSpotlightCollectionData(spotlightCollectionContentfulData)
    : null

  const showBfcmEmailOptIn = useFeature('siteExperience.isBFCM2024EmailSignUpEnabled') ?? false

  useEffect(() => {
    const spotlightGA4params = spotlightCollectionContentfulData?.analyticsTracking?.parameters
    if (spotlightGA4params) {
      analytics.fireGA4Event(spotlightGA4params)
    }
  }, [spotlightCollectionContentfulData])

  return (
    <Container size='xl' mb={5}>
      <ExpressDealDialogExperiment isOpen={isOpen} closeDialog={closeDialog} />
      <Box mb={[0, null, spotlightCollectionContentfulData || showBfcmEmailOptIn ? 4 : 1]} mt={4}>
        <TripDisplayOrder />
      </Box>
      <LayoutComponentWrapper>
        {showBfcmEmailOptIn && (
          <ComponentMetricsBoundary componentId='EmailSignUp'>
            <EmailSignUp appCode='DESKTOP' bfcmEmailOptIn sourceId='LANDING_HOME' />
          </ComponentMetricsBoundary>
        )}
        {mappedSpotlightCollectionData && (
          <ComponentMetricsBoundary componentId='SpotlightCollection'>
            <SpotlightCollection
              spotlightData={mappedSpotlightCollectionData}
              onClickCallToAction={() => {
                const ga4Parameters =
                  mappedSpotlightCollectionData?.callToAction?.analyticsTracking?.parameters
                if (ga4Parameters) {
                  analytics.fireGA4Event(ga4Parameters)
                }
                window.open(mappedSpotlightCollectionData?.callToAction?.url)
              }}
            />
          </ComponentMetricsBoundary>
        )}
      </LayoutComponentWrapper>
      <LayoutComponentWrapper topGap>
        <ContentfulOrderWrapper />
        <ComponentMetricsBoundary componentId='AppDownloadOptIn'>
          <AppDownloadOptIn />
        </ComponentMetricsBoundary>
      </LayoutComponentWrapper>
      <LayoutComponentWrapper topGap>
        {!showBfcmEmailOptIn && (
          <ComponentMetricsBoundary componentId='EmailSignUp'>
            <EmailSignUp appCode='DESKTOP' sourceId='LANDING_HOME' />
          </ComponentMetricsBoundary>
        )}
        <ComponentMetricsBoundary componentId='MediaCardCollection'>
          <MediaCardCollection collection={mediaCardCollectionData} cardNode={MediaCardComponentHorizon} />
        </ComponentMetricsBoundary>
        <ComponentMetricsBoundary componentId='PromoBanners'>
          <PromoBanners merchModules={promoItems} />
        </ComponentMetricsBoundary>
        <ComponentMetricsBoundary componentId='WhyPricelineBanner'>
          <WhyPricelineBanner content={REASONS_HOME} />
        </ComponentMetricsBoundary>
        <AdBannerContent />
        {skymondeSEO && (
          <ComponentMetricsBoundary componentId='LinksWidget'>
            <LinksWidget data={skymondeSEO} />
          </ComponentMetricsBoundary>
        )}
      </LayoutComponentWrapper>
    </Container>
  )
}
